import { useForm } from "@formiz/core";
import {
  compareErrorCode,
  IAuthenticatedUser,
  NotificationType,
  setOnboardingFinished,
  setShowOnboardingModal,
  useAppDispatch,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { UserErrorType } from "@next-platform/packages-ts-types";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

import useEnsureUserProfile from "@/features/user/utils/useEnsureUserProfile";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";
import { isUsernameValid } from "@/utils";

import useApi from "../useApi";
import GuidelinesModal from "./components/GuidelinesModal";
import WelcomeAboardModal from "./components/WelcomeAboardModal";
import { OnboardingStep } from "./types";
import { deleteOnboardingDraft, getOnboardingDraft, updateOnboardingDraft } from "./utils";

const logger = appLogger.withTag("Onboarding");

export function Onboarding() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userForm = useForm();
  const [debouncedUsername] = useDebounce(userForm.values?.username, 500);
  const { user } = useAuth();
  const { setAuthenticatedUser } = useEnsureUserProfile();
  const { onUserUpdateProfileV2, isLoading: isSubmitting } = useUserInfo();
  const { jsonApiService, callApi, isLoading: isCheckingUsername } = useApi();
  const { displayToast } = useToast();
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null);

  const { step = OnboardingStep.WELCOME_ABOARD } = getOnboardingDraft(user?.email) || {};
  const [selectedOnboardingStep, setSelectedOnboardingStep] = useState<OnboardingStep | null>(step as OnboardingStep);
  const formValues = Object.keys(userForm.values || {})?.length ? userForm.values : getOnboardingDraft(user?.email);
  const isLoading = isSubmitting || isCheckingUsername;

  const checkUsernameAvailability = async (username: string) => {
    try {
      const { isAvailable } = (await callApi(jsonApiService.checkUsername, username)) as { isAvailable: boolean };

      if (!isAvailable) {
        setSelectedOnboardingStep(OnboardingStep.WELCOME_ABOARD);
      }
      setIsUsernameAvailable(isAvailable);

      return { data: { isAvailable }, error: null };
    } catch (error) {
      logger.error(`error while checking username: ${username}`, error);
      if (selectedOnboardingStep === OnboardingStep.COMMUNITY_GUIDELINES) {
        displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
      }
      return { data: null, error };
    }
  };

  const submitForm = async () => {
    if (!userForm.isValid) {
      return setSelectedOnboardingStep(OnboardingStep.WELCOME_ABOARD);
    }
    const { data, error } = await checkUsernameAvailability(formValues.username);

    if (error) return;

    if (data?.isAvailable) {
      const payload = {
        username: formValues?.username,
        name: formValues?.name,
      };

      const { error, errorPayload } = await onUserUpdateProfileV2(payload);

      if (error) {
        const hasUsernameAlreadyTaken = compareErrorCode(errorPayload.errorCode, UserErrorType.USERNAME_DUPLICATED);
        displayToast({ message: error, type: NotificationType.error });

        if (hasUsernameAlreadyTaken) {
          setSelectedOnboardingStep(OnboardingStep.WELCOME_ABOARD);
          setIsUsernameAvailable(false);
        }
        return;
      }

      setTimeout(() => {
        deleteOnboardingDraft(user?.email);
      }, 500);
      setAuthenticatedUser({ ...user } as IAuthenticatedUser);
      setSelectedOnboardingStep(null);
      dispatch(setShowOnboardingModal(false));
      dispatch(setOnboardingFinished(true));
    }
  };

  useEffect(() => {
    if (debouncedUsername && isUsernameValid(debouncedUsername) && debouncedUsername.length >= 3 && isUsernameAvailable === null) {
      checkUsernameAvailability(debouncedUsername);
    }
  }, [debouncedUsername, callApi, jsonApiService.checkUsername]);

  useEffect(() => {
    setIsUsernameAvailable(null);
  }, [formValues?.username]);

  useEffect(() => {
    if (selectedOnboardingStep) {
      updateOnboardingDraft(user?.email, { step: selectedOnboardingStep });
    }
  }, [selectedOnboardingStep]);

  if (selectedOnboardingStep === OnboardingStep.WELCOME_ABOARD) {
    return (
      <WelcomeAboardModal
        isCheckingUsername={isCheckingUsername}
        isUsernameAvailable={isUsernameAvailable}
        userForm={userForm}
        setSelectedOnboardingStep={setSelectedOnboardingStep}
      />
    );
  }

  if (selectedOnboardingStep === OnboardingStep.COMMUNITY_GUIDELINES) {
    return <GuidelinesModal submitForm={submitForm} isSubmitting={isLoading} />;
  }

  return null;
}

export default Onboarding;
